import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { WorksProvider } from './contexts/WorksContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login/Login';
import Dashboard from "./pages/Dashboard/Dashboard";
import { UsersManagement } from './pages/UsersManagement/UsersManagement';
import { Settings } from './pages/Settings/Settings';
import NoticeManagement from './pages/Noticemanagement/NoticeMangement';

// Works Management Pages
import WorksDashboard from './pages/WorksManagement/WorksDashboard';
import WorksList from './pages/WorksManagement/WorksList';
import WorksFullFormat from './pages/WorksManagement/WorksFullFormat';
import RoadMileageFullFormat from './pages/WorksManagement/RoadMileageFullFormat';
import WorkForm from './pages/WorksManagement/WorkForm';
import WorkDetails from './pages/WorksManagement/WorkDetails';
import WorksAnalytics from './pages/WorksManagement/Analytics';
import WorksReports from './pages/WorksManagement/WorksReports';

// Territorial Drill-Down
import './index.css';
import MasterDataPage from './pages/MasterDataPage';
import VendorsManagement from './pages/VendorsManagement';
import StagesManagement from './pages/StagesManagement';
import AuthoritySlabsManagement from './pages/AuthoritySlabsManagement';
import ConstituenciesManagement from './pages/ConstituenciesManagement';
import DistrictsList from './components/District-driill-down/DistrictsList';
import PhysicalProgressDashboard from './pages/Dashboard/PhysicalProgressDashboard';
import MasterDataDashboard from './pages/Dashboard/MasterDataDashboard';
import TerritorialDrilldown from './pages/TerritorialDrilldown';
import HierarchicalWorksApp from './pages/HierarchicalWorksApp';
import HierarchicalDrillDown from './pages/HierarchicalDrillDown';
import PReDDashboard from './pages/dashboard/PReDDashboard';
import DashboardAbstract from './pages/DashboardReport/DashboardAbstract';
import TerritorialAbstract from './pages/DashboardReport/TerritorialAbstract';
import TerritorialPending from './pages/DashboardReport/TerritorialPending';
import StateLevelDashboard from './pages/DashboardReport/StateLevelDashboard';
import RoadMileageDashboard from './pages/DashboardReport/RoadMileageDashboard';
import PredBriefReport from './pages/DashboardReport/PredBriefReport';
import PendingTaskDetail from './pages/DashboardReport/PendingTaskDetail';
import WasteManagementSystem from './components/wms/WasteManagementSystem';
import WasteAgentApp from './components/wms/WasteAgentApp';
import WasteAgentLogin from './components/wms/WasteAgentlogin';
import WasteAgentloginAdmin from './components/wms/WasteAgentloginAdmin';

// Route wrapper: reads the work id from the URL and shows the details view
const WorkDetailsRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <WorkDetails
      workId={id ? Number(id) : 0}
      isOpen={true}
      onClose={() => navigate(-1)}
      title="Work Details"
      children={undefined}
    />
  );
};

// Create a separate component for the routes that uses useNavigate
const AppRoutes: React.FC = () => {
  const navigate = useNavigate();

  const handleLoginSuccess = (agent: any) => {
    console.log('Agent authenticated:', agent);
    localStorage.setItem('wasteAgent', JSON.stringify(agent));
    navigate('/wms');
  };

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/wms" element={<WasteManagementSystem />} />
      <Route path="/wmsapp" element={<WasteAgentApp />} />
      <Route 
        path="/wmsapplogin" 
        element={<WasteAgentLogin onLoginSuccess={handleLoginSuccess} />} 
      />

<Route 
        path="/wmsappadmin" 
        element={<WasteAgentloginAdmin onLoginSuccess={handleLoginSuccess} />} 
      />



      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardAbstract />} />
        <Route path="dashboard/territorial-abstract" element={<TerritorialAbstract />} />
        <Route path="dashboard/territorial-pending" element={<TerritorialPending />} />
        <Route path="dashboard/state-level" element={<StateLevelDashboard />} />
        <Route path="dashboard/road-mileage" element={<RoadMileageDashboard />} />
        <Route path="users" element={<UsersManagement />} />
        <Route path="settings" element={<Settings />} />
        <Route path="pred-brief" element={<PredBriefReport />} />
        <Route path="pending-tasks/:taskType" element={<PendingTaskDetail />} />
        <Route path="notices" element={<NoticeManagement />} />
        <Route path="masters" element={<MasterDataPage/>} />
        <Route path="vendors" element={<VendorsManagement/>} />
        <Route path="stages" element={<StagesManagement/>} />
        <Route path="authority-slabs" element={<AuthoritySlabsManagement/>} />
        <Route path="constituencies" element={<ConstituenciesManagement/>} />

        {/* Works Management Routes */}
        <Route path="works" element={<Navigate to="/works/list" replace />} />
        <Route path="works/dashboard" element={<WorksDashboard />} />
        <Route path="works/list" element={<WorksList />} />
        <Route path="works/full-format" element={<WorksFullFormat />} />
        <Route path="rm/full-format" element={<RoadMileageFullFormat />} />
        <Route path="works/drilldown" element={<HierarchicalWorksApp />} />
        <Route path="works/hierarchicaldrilldown" element={<HierarchicalDrillDown />} />
        <Route path="works/territorial-drilldown" element={<TerritorialDrilldown />} />
        <Route path="works/PReDDashboard" element={<PReDDashboard />} />
        <Route path="works/analytics" element={<WorksAnalytics />} />
        <Route path="works/reports" element={<WorksReports />} />
        <Route path="works/reports/districts" element={<DistrictsList />} />
        <Route path="works/reports/progressdashboard" element={<PhysicalProgressDashboard />} />
        <Route path="works/reports/MasterDataDashboard" element={<MasterDataDashboard />} />
        <Route path="works/reports/dashboard-abstract" element={<DashboardAbstract />} />
        <Route path="works/reports/pred-brief" element={<PredBriefReport />} />
        <Route path="works/reports/pending-tasks/:taskType" element={<PendingTaskDetail />} />
        <Route path="works/create" element={<WorkForm mode="create" />} />
        <Route path="works/edit/:id" element={<WorkForm mode="edit" />} />
        <Route path="works/view/:id" element={<WorkDetailsRoute />} />

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />                
      </Route>
    </Routes>
  );
};

// Main App component that provides the Router context
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <WorksProvider>
            <AppRoutes />
          </WorksProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;